import React, { useState, useEffect } from "react";
import {
  Award,
  Plus,
  UserPlus,
  Edit3,
  UserMinus,
  GraduationCap,
  Utensils,
  Globe,
  Layout,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { DashboardService } from "../../services/dashboard";

const getCategory = (action) => {
  if (
    [
      "update_lesson_progress",
      "create_lesson",
      "badge_awarded",
      "auto_assign_lesson",
      "training_overdue",
    ].includes(action)
  )
    return "training";
  if (
    [
      "dish_created",
      "dish_updated",
      "dish_deleted",
      "dish_restored",
      "wine_created",
      "wine_updated",
      "wine_deleted",
      "wine_restored",
      "beer_created",
      "beer_updated",
      "beer_archived",
      "beer_restored",
    ].includes(action)
  )
    return "menu";
  if (
    ["user_registered", "login", "login_failed", "user_deactivated"].includes(
      action
    )
  )
    return "staff";

  if (["create_restaurant_success", "restaurant_created"].includes(action)) {
    return "platform";
  }

  return "platform";
};

const getIconStyles = (action) => {
  if (action === "badge_awarded") {
    return {
      bgColor: "bg-[#B91C1C]",
      icon: <Award className="w-4 h-4 text-white" />,
    };
  }
  if (
    action === "restaurant_created" ||
    action === "create_restaurant_success"
  ) {
    return {
      bgColor: "bg-[#047857]",
      icon: <Plus className="w-4 h-4 text-white" />,
    };
  }
  if (action === "user_registered") {
    return {
      bgColor: "bg-[#2563EB]",
      icon: <UserPlus className="w-4 h-4 text-white" />,
    };
  }
  if (action.includes("dish_") || action.includes("wine_") || action.includes("beer_")) {
    return {
      bgColor: "bg-[#92400E]",
      icon: <Edit3 className="w-4 h-4 text-white" />,
    };
  }
  if (action === "user_deactivated") {
    return {
      bgColor: "bg-[#2563EB]",
      icon: <UserMinus className="w-4 h-4 text-white" />,
    };
  }

  // Fallbacks by category
  const category = getCategory(action);
  if (category === "training")
    return {
      bgColor: "bg-[#B91C1C]",
      icon: <GraduationCap className="w-4 h-4 text-white" />,
    };
  if (category === "menu")
    return {
      bgColor: "bg-[#92400E]",
      icon: <Utensils className="w-4 h-4 text-white" />,
    };
  if (category === "staff")
    return {
      bgColor: "bg-[#2563EB]",
      icon: <Users className="w-4 h-4 text-white" />,
    };
  return {
    bgColor: "bg-[#047857]",
    icon: <Layout className="w-4 h-4 text-white" />,
  };
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "just now";
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800)
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

const generateLogTitle = (log) => {
  const details = log.details || {};
  const action = log.action;
  const user = log.user_uuid
    ? `${log.user_uuid.first_name || ""} ${
        log.user_uuid.last_name || ""
      }`.trim()
    : details.user_role || "User";

  const timeSpan = (
    <span className="text-[11px] font-medium text-gray-400 ml-2 whitespace-nowrap lowercase">
      {formatTimeAgo(log.timestamp)}
    </span>
  );

  if (action === "badge_awarded") {
    return (
      <p className="text-[13px] text-gray-500 leading-snug">
        <span className="font-semibold text-[#1F2937]">{user}</span>{" "}
        <span className="font-semibold text-[#1F2937]">
          {details.badge_name || "a badge"}
        </span>
        {timeSpan}
      </p>
    );
  }

  let logContent = null;
  if (
    action === "create_restaurant_success" ||
    action === "restaurant_created"
  ) {
    logContent = (
      <p className="text-[13px] text-gray-500 leading-snug">
        <span className="font-semibold text-[#1F2937]">
          "{details.restaurant_name || details.description || "New Site"}"
        </span>
        {timeSpan}
      </p>
    );
  } else if (action === "user_registered") {
    logContent = (
      <p className="text-[13px] text-gray-500 leading-snug">
        <span className="font-semibold text-[#1F2937]">
          {details.employee_name || user}
        </span>
        {timeSpan}
      </p>
    );
  } else if (
    action.includes("dish_created") ||
    action.includes("dish_updated") ||
    action.includes("dish_") ||
    action.includes("wine_") ||
    action.includes("beer_")
  ) {
    logContent = (
      <p className="text-[13px] text-gray-500 leading-snug">
        <span className="font-semibold text-[#1F2937]">
          {details.dish_name || details.description || "New Item"}
        </span>
        {timeSpan}
      </p>
    );
  } else if (action === "update_lesson_progress" && details.lesson) {
    // details.lesson is attached by the logs API; older entries use chapterPassed.
    const lesson = details.lesson;
    const category = lesson.category ? lesson.category.charAt(0).toUpperCase() + lesson.category.slice(1) : "";
    const completed = details.isCompleted ?? details.chapterPassed;
    logContent = (
      <p className="text-[13px] text-gray-500 leading-snug">
        <span className="font-semibold text-[#1F2937]">{user}</span>{" "}
        {completed ? "completed" : "worked on"}{" "}
        <span className="font-semibold text-[#1F2937]">
          {`${category} Unit ${lesson.unit}, Chapter ${lesson.chapter}${lesson.chapter_name ? `: ${lesson.chapter_name}` : ""}`.trim()}
        </span>
        {timeSpan}
      </p>
    );
  } else if (action === "user_deactivated") {
    logContent = (
      <p className="text-[13px] text-gray-500 leading-snug">
        <span className="font-semibold text-[#1F2937]">
          {details.employee_name || user}
        </span>
        {timeSpan}
      </p>
    );
  } else {
    logContent = (
      <p className="font-semibold text-[#1F2937] text-[13px] text-gray-500 leading-snug">
        {details.description || action.replace(/_/g, " ")}
        {timeSpan}
      </p>
    );
  }

  return logContent;
};

const OrganizationActivityFeed = () => {
  const [loading, setLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        // If a specific category is selected, request a larger batch and filter client-side
        if (filter && filter !== "all") {
          // Try to fetch many logs in one request to improve chances of matching category
          const fetchLimit = 1000;
          const dataAll = await DashboardService.getActivityLogs(1, fetchLimit);
          const allLogs = dataAll.logs || [];
          const filtered = allLogs.filter(
            (l) => getCategory(l.action) === filter
          );
          setActivityLogs(filtered.slice(0, itemsPerPage));
          // Compute pages for the filtered set
          setTotalPages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
        } else {
          // Default: paginated server fetch for the full feed
          const data = await DashboardService.getActivityLogs(
            currentPage,
            itemsPerPage
          );
          const logs = data.logs || [];
          setActivityLogs(logs);
          setTotalPages(data.pagination?.totalPages || 1);
        }
      } catch (err) {
        console.error("Failed to fetch activity logs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [filter, currentPage]);

  // When the filter changes, show the first page to keep UX consistent
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const categories = [
    {
      id: "training",
      label: "Training",
      icon: <GraduationCap size={14} />,
      bg: "bg-rose-50",
      text: "text-rose-700",
    },
    {
      id: "menu",
      label: "Menu Updates",
      icon: <Utensils size={14} />,
      bg: "bg-amber-50",
      text: "text-amber-700",
    },
    {
      id: "staff",
      label: "Staff",
      icon: <UserPlus size={14} />,
      bg: "bg-blue-50",
      text: "text-blue-700",
    },
    {
      id: "all",
      label: "All Events",
      icon: <Globe size={14} />,
      bg: "bg-emerald-50",
      text: "text-emerald-700",
    },
  ];

  const getSubtext = (log) => {
    if (
      log.action === "create_restaurant_success" ||
      log.action === "restaurant_created"
    )
      return "Platform Update";
    return log.restaurant_uuid?.name || "Global Activity";
  };

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 font-sans">
      <div className="flex flex-col text-start lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
        <h2 className="text-xl font-black text-[#1F2937] tracking-tighter">
          Organization Activity Feed
        </h2>

        <div className="flex flex-wrap gap-3">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                // Always set the selected category on click (no toggle on double-click)
                setFilter(cat.id);
                setCurrentPage(1);
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] font-semibold border transition-all shadow-sm whitespace-nowrap ${
                filter === cat.id
                  ? `${cat.bg} ${cat.text} border-transparent shadow-[#00000005]`
                  : "bg-white border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
              }`}
            >
              <span>{cat.icon}</span>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4 text-start min-h-[400px]">
        {loading ? (
          [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="flex gap-4 animate-pulse pt-2">
              <div className="w-8 h-8 bg-gray-100 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-50 rounded w-3/4"></div>
                <div className="h-3 bg-gray-50 rounded w-1/4"></div>
              </div>
            </div>
          ))
        ) : activityLogs.length === 0 ? (
          <div className="text-center py-20 text-gray-400 font-bold text-sm">
            No recent organization activity
          </div>
        ) : (
          activityLogs.map((log, idx) => {
            const styles = getIconStyles(log.action);
            return (
              <div key={idx} className="flex gap-4 items-start py-1">
                <div
                  className={`w-8 h-8 rounded-full ${styles.bgColor} flex items-center justify-center shrink-0 shadow-sm mt-0.5`}
                >
                  {styles.icon}
                </div>
                <div className="flex-1 min-w-0">
                  {generateLogTitle(log)}
                  <p className="text-[11px] font-bold text-gray-400 tracking-tight mt-0.5">
                    {getSubtext(log)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-50 mt-8">
          <p className="text-[11px] font-bold text-gray-400">
            Page{" "}
            <span className="text-[#1F2937] font-black">{currentPage}</span> of{" "}
            <span className="text-[#1F2937] font-black">{totalPages}</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black transition-all border ${
                currentPage === 1
                  ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                  : "bg-white border-gray-100 text-gray-500 hover:bg-gray-50 shadow-sm"
              }`}
            >
              <ChevronLeft size={14} />
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`flex items-center gap-2 px-6 py-2 rounded-xl text-[11px] font-black transition-all border ${
                currentPage === totalPages
                  ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                  : "bg-white border-gray-100 text-[#1F2937] hover:bg-gray-50 shadow-sm"
              }`}
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationActivityFeed;
